import { useCallback } from 'react';
import { create } from 'zustand';
import { getMaintenanceUnits } from 'services/asset';
import { CalculationType } from 'app/pages/ValidationsPage/type';

// ======================
// Types
// ======================

export interface MaintenanceUnitOption {
  id: number;
  name: string;
}

// GET /maintenance-units returns units pre-grouped by calculation type
// (DATE_BASED / COUNTER_BASED) rather than a flat list.
export interface MaintenanceUnitGroup {
  name: string;
  code: CalculationType;
  units: MaintenanceUnitOption[];
}

interface MaintenanceUnitStore {
  maintenanceUnits: MaintenanceUnitGroup[];
  setMaintenanceUnits: (groups: MaintenanceUnitGroup[]) => void;
}

// ======================
// Store
// ======================

// Not paginated — the response is the full static reference list, so unlike
// triggerType/triggerSubType there's no force-insert-on-edit hook needed here.
export const useMaintenanceUnitStore = create<MaintenanceUnitStore>((set) => ({
  maintenanceUnits: [],
  setMaintenanceUnits: (maintenanceUnits) => set({ maintenanceUnits })
}));

// ======================
// Hooks
// ======================

export const useFetchMaintenanceUnits = () => {
  const { setMaintenanceUnits } = useMaintenanceUnitStore();

  const init = useCallback(async () => {
    try {
      const data = await getMaintenanceUnits();
      setMaintenanceUnits(data.maintenanceUnits || []);
    } catch (error) {
      console.error('Failed to fetch maintenance units:', error);
    }
  }, [setMaintenanceUnits]);

  return init;
};

// ======================
// Helpers
// ======================

// A config's Unit dropdown is scoped to whichever calculation type the
// selected trigger type carries — COUNTER_BASED configs only see
// COUNTER_BASED units, DATE_BASED only DATE_BASED.
export const getUnitOptionsForCalculationType = (
  groups: MaintenanceUnitGroup[],
  calculationType: CalculationType | '' | null | undefined
): { label: string; value: string }[] => {
  if (!calculationType) return [];
  const group = groups.find((g) => g.code === calculationType);
  return (group?.units ?? []).map((u) => ({ label: u.name, value: String(u.id) }));
};

// A maintenance config's `unitId` is submitted as just the unit id (a plain
// number, also what GET responses return for this same field — GET
// separately includes a populated `unit: { id, name }` relation for display
// purposes only). Mirrors normalizeTriggerSubTypeId / normalizeTriggerTypeId.
export const normalizeUnitId = (
  raw: { id: number } | number | string | null | undefined
): string | null => {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return String(raw.id);
  return String(raw);
};
